import type { ThemePreference } from "../../../shared/mutators/setUserPreference";
import { PageSurface } from "../PageBody";
import { config, resolveManagedChatModel } from "../../environment";
import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useState } from "react";
import {
  defaultChatModel,
  MANAGED_AI_PROVIDER_ID,
  resolveAIProviderChatModel,
} from "../../../shared/aiProviders";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { buildModelPickerItems } from "../modelPickerItems";
import { usePersonalWorkEnabled } from "../../hooks/usePersonalWorkEnabled";
import { useScopedModel } from "../../hooks/useScopedModel";
import { requestChatBrowserNotificationPermission } from "../../lib/browserNotifications";
import { formatModelName } from "../../lib/formatModelName";
import { useFormatRelativeTime } from "../../hooks/useFormatRelativeTime";
import { formatDateShort } from "../../lib/formatTimestamp";
import { resolveDefaultPlanMode } from "../../lib/planMode";
import { Box } from "../Box";
import { Button } from "../Button";
import { ConfirmModal } from "../ConfirmModal";
import { Dropdown } from "../Dropdown";
import { Icon, type IconName } from "../Icon";
import { PressButton } from "../PressButton";
import { ProviderLogo } from "../ProviderLogo";
import { Text } from "../Text";
import { Toggle } from "../Toggle";
import { DropdownOpener, SectionHeading } from "./shared";

const Container = styled("div")`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const Card = styled(PageSurface)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 0;
  gap: 24px;
  padding: 64px 24px;
  overflow: auto;
  box-shadow: var(--elevation-low);
  background: var(--surface-primary);
  border-radius: 16px;
  outline: 0.5px solid var(--border-alpha);
`;

const Header = styled("div")`
  width: 100%;
  max-width: 648px;
  padding-left: 24px;
  margin-bottom: 8px;
`;

const ContentBox = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 648px;
  padding: 24px;
  background: var(--surface-secondary);
  border-radius: 12px;
  border: 0.5px solid var(--border-alpha);
`;

const Section = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Field = styled("div")`
  display: flex;
  flex-direction: column;
`;

const ToggleRow = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ToggleGroup = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background-color: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 8px;
`;

const defaultViewOptions: {
  value: "recents" | "chats";
  label: string;
  icon: IconName;
}[] = [
  { value: "recents", label: "Home", icon: "tines-mark" },
  { value: "chats", label: "Chat", icon: "ask" },
];

const followUpOptions: {
  value: "queue" | "steer";
  label: string;
  icon: IconName;
}[] = [
  { value: "queue", label: "Queue", icon: "list-ordered" },
  { value: "steer", label: "Steer", icon: "joystick" },
];

const IconTabContainer = styled("div")`
  display: flex;
  align-items: center;
  align-self: flex-start;
  height: 32px;
  background: var(--alpha4);
  border-radius: 8px;
  padding: 0 2px;
  position: relative;
`;

const IconTabIndicator = styled("div")`
  position: absolute;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
  outline: 0.5px solid var(--border-alpha);
  transition: transform var(--motion-fast) var(--ease-out);
  pointer-events: none;
`;

const IconTab = styled(PressButton)`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
  background: transparent;
  color: var(--text-secondary);
  opacity: 0.5;
  position: relative;
  z-index: 1;
  &:hover {
    color: var(--text-primary);
    opacity: 1;
  }
  &[data-active="true"] {
    color: var(--text-primary);
    opacity: 1;
  }
`;

export function PreferencesSettings() {
  const formatRelativeTime = useFormatRelativeTime();
  const z = useZero();
  const [preferences] = useQuery(queries.userPreferences());
  const [oauthGrants] = useQuery(queries.myOAuthGrants());
  const [revokeGrantId, setRevokeGrantId] = useState<string | null>(null);
  const personalWorkEnabled = usePersonalWorkEnabled() === true;
  const defaultView =
    preferences?.defaultHome === "chats" ? "chats" : "recents";
  const followUpBehavior =
    preferences?.followUpBehavior === "steer" ? "steer" : "queue";
  const soundClicks = preferences?.soundClicks !== "off";
  const soundChat = preferences?.soundChat !== "off";
  const soundWorkflow = preferences?.soundWorkflow !== "off";
  const browserNotifications = preferences?.browserNotifications === "on";
  const follow3bByDefault = preferences?.follow3bByDefault === "on";
  const suggestFollow3b = preferences?.suggestFollow3b !== "off";
  const planModeWorkflowDefault = resolveDefaultPlanMode(
    preferences,
    "workflow"
  );
  const planModeBranchDefault = resolveDefaultPlanMode(preferences, "branch");
  const theme = (preferences?.theme as ThemePreference) ?? "system";

  const setPreference = (
    updates: Parameters<
      (typeof sharedMutators)["setUserPreference"]["fn"]
    >[0]["args"]
  ) => {
    z.mutate(sharedMutators.setUserPreference(updates));
  };

  const modelScoped = useScopedModel({ type: "personal" });
  const selectedManagedModel = modelScoped.managedProvider
    ? resolveManagedChatModel(
        preferences?.chatModel ?? modelScoped.managedDefaultChatModel
      )
    : null;
  const selectedModelId = modelScoped.effectiveConfig
    ? resolveAIProviderChatModel(
        modelScoped.effectiveConfig,
        modelScoped.effectiveConfig.appUsage?.connectedApp?.configuration,
        config.managedChatModels,
        preferences?.chatModel
      )
    : modelScoped.effectiveKey === MANAGED_AI_PROVIDER_ID &&
        selectedManagedModel
      ? selectedManagedModel
      : defaultChatModel(modelScoped.providerId);
  const modelItems = buildModelPickerItems({
    managedProvider: modelScoped.managedProvider,
    configs: modelScoped.configs,
    tenantDefaultKey: modelScoped.tenantDefaultKey,
    effectiveKey: modelScoped.effectiveKey,
    managedChatModel: selectedManagedModel,
    managedDefaultChatModel: modelScoped.managedDefaultChatModel,
    configChatModel: preferences?.chatModel ?? null,
    onSelectManaged: (modelId) =>
      setPreference({
        aiProviderId: MANAGED_AI_PROVIDER_ID,
        chatModel: modelId,
      }),
    onSelectConfig: (configId, modelId) =>
      setPreference({ aiProviderId: configId, chatModel: modelId }),
  });
  const hasModelPreference =
    preferences?.aiProviderId != null || preferences?.chatModel != null;

  return (
    <Container>
      <Card>
        <Header>
          <Text variant="display-md">Preferences</Text>
          <div style={{ marginTop: 2 }}>
            <Text variant="text-regular" color="var(--text-secondary)">
              Customize how 3B looks, sounds, and behaves for you.
            </Text>
          </div>
        </Header>

        <ContentBox>
          <SectionHeading>
            <Text variant="text-regular-md">General</Text>
          </SectionHeading>
          <Section>
            {personalWorkEnabled && (
              <Field>
                <Box horizontal={8} vertical={10}>
                  <Text variant="text-small-md">Default view</Text>
                </Box>
                <Dropdown
                  opener={
                    <DropdownOpener style={{ width: 208 }}>
                      <Text variant="text-regular">
                        {defaultView === "chats" ? "Chat" : "Home"}
                      </Text>
                      <span style={{ opacity: 0.3 }}>
                        <Icon name="chevron-down" size={12} />
                      </span>
                    </DropdownOpener>
                  }
                  align="bottom-left"
                  sections={[
                    {
                      items: defaultViewOptions.map((opt) => ({
                        type: "button" as const,
                        left: <Icon name={opt.icon} size={16} />,
                        label: opt.label,
                        right:
                          opt.value === defaultView ? (
                            <Icon
                              name="check"
                              size={12}
                              color="var(--text-secondary)"
                            />
                          ) : undefined,
                        onClick: () =>
                          setPreference({ defaultHome: opt.value }),
                      })),
                    },
                  ]}
                />
                <Box horizontal={8} top={6}>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Choose whether 3B opens to Home or Chat
                  </Text>
                </Box>
              </Field>
            )}
            <Field>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Theme</Text>
              </Box>
              <IconTabContainer>
                <IconTabIndicator
                  style={{
                    transform: `translateX(${
                      theme === "system" ? 0 : theme === "light" ? 28 : 56
                    }px)`,
                  }}
                />
                {(
                  [
                    { value: "system", icon: "monitor-smartphone" },
                    { value: "light", icon: "sun" },
                    { value: "dark", icon: "moon" },
                  ] as const
                ).map((opt) => (
                  <IconTab
                    key={opt.value}
                    data-active={theme === opt.value}
                    onPress={() => setPreference({ theme: opt.value })}
                  >
                    <Icon name={opt.icon} size={16} />
                  </IconTab>
                ))}
              </IconTabContainer>
              <Box horizontal={8} top={6}>
                <Text variant="text-small" color="var(--text-secondary)">
                  Choose between light, dark, or system theme
                </Text>
              </Box>
            </Field>
          </Section>
        </ContentBox>
        <ContentBox>
          <SectionHeading>
            <Text variant="text-regular-md">Chat</Text>
          </SectionHeading>
          <Section>
            {modelItems.length > 0 && (
              <Field>
                <Box horizontal={8} vertical={10}>
                  <Text variant="text-small-md">Preferred model</Text>
                </Box>
                <Dropdown
                  opener={
                    <DropdownOpener style={{ width: 208 }}>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 8,
                          minWidth: 0,
                        }}
                      >
                        <ProviderLogo
                          provider={modelScoped.providerId}
                          size={16}
                        />
                        <Text variant="text-regular" ellipsis>
                          {formatModelName(selectedModelId)}
                        </Text>
                      </span>
                      <span style={{ opacity: 0.3 }}>
                        <Icon name="chevron-down" size={12} />
                      </span>
                    </DropdownOpener>
                  }
                  align="bottom-left"
                  sections={[
                    { items: modelItems },
                    ...(hasModelPreference
                      ? [
                          {
                            items: [
                              {
                                key: "use-default",
                                type: "button" as const,
                                left: null,
                                label: "Use default model",
                                onClick: () =>
                                  setPreference({
                                    aiProviderId: null,
                                    chatModel: null,
                                  }),
                              },
                            ],
                          },
                        ]
                      : []),
                  ]}
                />
                <Box horizontal={8} top={6}>
                  <Text variant="text-small" color="var(--text-secondary)">
                    The model your chats use by default. You can still switch
                    models in any chat.
                  </Text>
                </Box>
              </Field>
            )}

            <Field>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Chat follow-up behavior</Text>
              </Box>
              <Dropdown
                opener={
                  <DropdownOpener style={{ width: 208 }}>
                    <Text variant="text-regular">
                      {followUpBehavior === "steer" ? "Steer" : "Queue"}
                    </Text>
                    <span style={{ opacity: 0.3 }}>
                      <Icon name="chevron-down" size={12} />
                    </span>
                  </DropdownOpener>
                }
                align="bottom-left"
                sections={[
                  {
                    items: followUpOptions.map((opt) => ({
                      type: "button" as const,
                      left: <Icon name={opt.icon} size={16} />,
                      label: opt.label,
                      right:
                        opt.value === followUpBehavior ? (
                          <Icon
                            name="check"
                            size={12}
                            color="var(--text-secondary)"
                          />
                        ) : undefined,
                      onClick: () =>
                        setPreference({ followUpBehavior: opt.value }),
                    })),
                  },
                ]}
              />
              <Box horizontal={8} top={6}>
                <Text variant="text-small" color="var(--text-secondary)">
                  Queue messages to send after the agent finishes, or steer the
                  agent mid-turn. Use ⌘↵ to{" "}
                  {followUpBehavior === "steer" ? "queue" : "steer"}
                </Text>
              </Box>
            </Field>

            <Field>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Plan mode</Text>
              </Box>
              <ToggleGroup>
                <ToggleRow>
                  <Text variant="text-regular">
                    New workflows start in plan mode
                  </Text>
                  <Toggle
                    isOn={planModeWorkflowDefault}
                    onToggle={() =>
                      setPreference({
                        planModeWorkflowDefault: planModeWorkflowDefault
                          ? "off"
                          : "on",
                      })
                    }
                  />
                </ToggleRow>
                <ToggleRow>
                  <Text variant="text-regular">
                    New branches start in plan mode
                  </Text>
                  <Toggle
                    isOn={planModeBranchDefault}
                    onToggle={() =>
                      setPreference({
                        planModeBranchDefault: planModeBranchDefault
                          ? "off"
                          : "on",
                      })
                    }
                  />
                </ToggleRow>
              </ToggleGroup>
            </Field>

            <Field>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Follow 3B</Text>
              </Box>
              <ToggleGroup>
                <ToggleRow>
                  <Text variant="text-regular">Follow 3B by default</Text>
                  <Toggle
                    isOn={follow3bByDefault}
                    onToggle={() =>
                      setPreference({
                        follow3bByDefault: follow3bByDefault ? "off" : "on",
                      })
                    }
                  />
                </ToggleRow>
                <ToggleRow>
                  <Text variant="text-regular">
                    Suggest following on a new chat
                  </Text>
                  <Toggle
                    isOn={suggestFollow3b}
                    onToggle={() =>
                      setPreference({
                        suggestFollow3b: suggestFollow3b ? "off" : "on",
                      })
                    }
                  />
                </ToggleRow>
              </ToggleGroup>
              <Box horizontal={8} top={6}>
                <Text variant="text-small" color="var(--text-secondary)">
                  When you’re following, the workflow view jumps to the step 3B
                  is editing.
                </Text>
              </Box>
            </Field>
          </Section>
        </ContentBox>
        <ContentBox>
          <SectionHeading>
            <Text variant="text-regular-md">Sounds and notifications</Text>
          </SectionHeading>
          <Section>
            <Field>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Sounds</Text>
              </Box>
              <ToggleGroup>
                <ToggleRow>
                  <Text variant="text-regular">Chat sounds</Text>
                  <Toggle
                    isOn={soundChat}
                    onToggle={() =>
                      setPreference({ soundChat: soundChat ? "off" : "on" })
                    }
                  />
                </ToggleRow>
                <ToggleRow>
                  <Text variant="text-regular">UI clicks</Text>
                  <Toggle
                    isOn={soundClicks}
                    onToggle={() =>
                      setPreference({
                        soundClicks: soundClicks ? "off" : "on",
                      })
                    }
                  />
                </ToggleRow>
                <ToggleRow>
                  <Text variant="text-regular">Workflow sounds</Text>
                  <Toggle
                    isOn={soundWorkflow}
                    onToggle={() =>
                      setPreference({
                        soundWorkflow: soundWorkflow ? "off" : "on",
                      })
                    }
                  />
                </ToggleRow>
              </ToggleGroup>
              <Box horizontal={8} top={6}>
                <Text variant="text-small" color="var(--text-secondary)">
                  Control which sounds play throughout the app
                </Text>
              </Box>
            </Field>

            <Field>
              <Box horizontal={8} vertical={10}>
                <Text variant="text-small-md">Browser notifications</Text>
              </Box>
              <ToggleGroup>
                <ToggleRow>
                  <Text variant="text-regular">
                    Notify when 3B is finished working or needs input in a
                    background browser tab
                  </Text>
                  <Toggle
                    isOn={browserNotifications}
                    onToggle={async () => {
                      const next = browserNotifications ? "off" : "on";
                      if (next === "on") {
                        const permission =
                          await requestChatBrowserNotificationPermission();
                        if (permission !== "granted") return;
                      }
                      setPreference({ browserNotifications: next });
                    }}
                  />
                </ToggleRow>
              </ToggleGroup>
            </Field>
          </Section>
        </ContentBox>
        {oauthGrants.length > 0 && (
          <ContentBox>
            <SectionHeading>
              <Text variant="text-regular-md">Connected accounts</Text>
            </SectionHeading>
            <Section>
              <Field>
                <Box horizontal={8} vertical={10}>
                  <Text variant="text-small-md">OAuth apps</Text>
                </Box>
                <ToggleGroup>
                  {oauthGrants.map((grant) => (
                    <ToggleRow key={grant.id}>
                      <Box gap={2}>
                        <Text variant="text-regular">
                          {grant.client?.name ?? grant.clientId}
                        </Text>
                        {grant.client?.clientIdUrl && (
                          <Text
                            variant="text-small"
                            color="var(--text-secondary)"
                          >
                            {grant.client.clientIdUrl}
                          </Text>
                        )}
                        <Text
                          variant="text-small"
                          color="var(--text-secondary)"
                        >
                          {[
                            grant.createdAt &&
                              `Authorized ${formatDateShort(grant.createdAt)}`,
                            grant.lastUsedAt
                              ? `Last used ${formatRelativeTime(grant.lastUsedAt)}`
                              : "Never used",
                            grant.scopes.join(", "),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </Text>
                      </Box>
                      <Button
                        size="m"
                        variant="secondary"
                        onClick={() => setRevokeGrantId(grant.id)}
                      >
                        Revoke
                      </Button>
                    </ToggleRow>
                  ))}
                </ToggleGroup>
                <Box horizontal={8} top={6}>
                  <Text variant="text-small" color="var(--text-secondary)">
                    Apps you’ve authorized via OAuth that can act as you. Revoke
                    to cut off their access immediately.
                  </Text>
                </Box>
              </Field>
            </Section>
          </ContentBox>
        )}
      </Card>
      {revokeGrantId && (
        <ConfirmModal
          open
          onClose={() => setRevokeGrantId(null)}
          onConfirm={() => {
            z.mutate(sharedMutators.revokeOAuthGrant({ id: revokeGrantId }));
            setRevokeGrantId(null);
          }}
          title="Revoke app access"
          description="This app will lose access immediately, and any of its active tokens acting as you stop working. This can’t be undone."
          confirmLabel="Revoke"
          destructive
        />
      )}
    </Container>
  );
}
